import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';
import { SettingsCard, SettingsGrid, SettingsHelp, SettingsPage } from '../../components/settings/SettingsPage';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

function resizeImage(file: File, maxSize: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxSize || h > maxSize) {
          if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
          else { w = Math.round(w * maxSize / h); h = maxSize; }
        }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Field names match V2 /settings/company-profile contract exactly.
//
// Only the fields something downstream reads. The endpoint also accepts
// wcb_number, billing_country, physical_same_as_billing and the five physical_*
// columns; they save, and nothing — no PDF, email, report or screen — reads
// them back. They are left out of this form rather than shown as settings that
// do nothing, and left out of the PATCH so a save never touches them. To bring
// one back, wire up its reader first.
interface ProfileForm {
  name: string;
  phone: string;
  // Letterhead address printed on invoices, quotes, BOLs and weight tickets, and
  // snapshotted onto invoices.company_email at creation. NOT the sender identity
  // and NOT email_reply_to — that one lives on the Email Branding page.
  email: string;
  website: string;
  logo_url: string;
  billing_street: string;
  billing_city: string;
  billing_province_state: string;
  billing_postal_code: string;
}

const DEFAULTS: ProfileForm = {
  name: '', phone: '', email: '', website: '', logo_url: '',
  billing_street: '', billing_city: '', billing_province_state: '', billing_postal_code: '',
};

export default function CompanyProfilePage() {
  const queryClient = useQueryClient();
  const logoRef = useRef<HTMLInputElement>(null);
  const [initialized, setInitialized] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'company-profile'],
    queryFn: () => apiClient.get('/settings/company-profile').then(r => r.data?.data ?? r.data ?? {}),
  });

  const [form, setForm] = useState<ProfileForm>(DEFAULTS);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Seed form from GET response exactly once
  useEffect(() => {
    if (data && !initialized) {
      const merged = { ...DEFAULTS } as any;
      for (const key of Object.keys(DEFAULTS)) {
        if ((data as any)[key] !== undefined && (data as any)[key] !== null) {
          merged[key] = (data as any)[key];
        }
      }
      setForm(merged);
      setLogoPreview(merged.logo_url || null);
      setInitialized(true);
      setDirty(false);
    }
  }, [data, initialized]);

  const update = (key: keyof ProfileForm, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: ProfileForm) => apiClient.patch('/settings/company-profile', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'company-profile'] });
      setDirty(false);
      toast.success('Company profile saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a PNG, JPG, or WebP image.'); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5MB.'); return; }
    try {
      const resized = await resizeImage(file, 200);
      setLogoPreview(resized);
      update('logo_url', resized);
    } catch { toast.error('Failed to process image.'); }
    e.target.value = '';
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  return (
    // The Organization workspace renders the title and the tab strip.
    <SettingsPage>
      <SettingsGrid>
        <SettingsCard title="Company details">
          {/* Logo */}
          <div>
            <label className="text-sm font-medium text-white block mb-2">Company Logo</label>
            <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoUpload} />
            <button type="button" onClick={() => logoRef.current?.click()}
              className="w-24 h-24 bg-fieldiq-dark border-2 border-dashed border-fieldiq-border rounded-lg flex flex-col items-center justify-center gap-2 hover:border-fieldiq-accent transition-colors overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-1" />
              ) : (
                <><Camera size={24} className="text-fieldiq-muted" /><span className="text-xs text-fieldiq-text">Upload</span></>
              )}
            </button>
            {logoPreview && (
              <button onClick={() => { setLogoPreview(null); update('logo_url', ''); }} className="text-xs text-red-400 mt-1 hover:underline">Remove logo</button>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-white block mb-1">Company Name</label>
            <input className={inputCls} value={form.name} onChange={e => update('name', e.target.value)} placeholder="Apex Hauling Ltd" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1">Phone</label>
              <input className={inputCls} value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="403-555-1234" />
            </div>
            <div>
              <label className="text-sm font-medium text-white block mb-1">Email shown on invoices and tickets</label>
              <input type="email" className={inputCls} value={form.email} onChange={e => update('email', e.target.value)} placeholder="accounting@company.com" />
              <SettingsHelp className="mt-1">Printed on invoices, quotes, BOLs and weight tickets; replies go to the Email Branding address.</SettingsHelp>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-white block mb-1">Website</label>
            <input className={inputCls} value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://company.com" />
          </div>
        </SettingsCard>

        <SettingsCard title="Billing Address">
          <input className={inputCls} value={form.billing_street} onChange={e => update('billing_street', e.target.value)} placeholder="Street address" />
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} value={form.billing_city} onChange={e => update('billing_city', e.target.value)} placeholder="City" />
            <input className={inputCls} value={form.billing_province_state} onChange={e => update('billing_province_state', e.target.value)} placeholder="Province/State" />
          </div>
          <input className={inputCls} value={form.billing_postal_code} onChange={e => update('billing_postal_code', e.target.value)} placeholder="Postal/ZIP" />
        </SettingsCard>
      </SettingsGrid>

      {/* Save — one Save for both cards, as before. */}
      <div className="flex justify-end">
        <button
          onClick={() => saveMutation.mutate(form)}
          disabled={!dirty || saveMutation.isPending}
          className={`w-full min-[1440px]:w-auto min-[1440px]:px-10 h-12 rounded-lg font-semibold text-base transition-colors flex items-center justify-center gap-2 ${
            dirty
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-fieldiq-muted cursor-not-allowed'
          }`}
        >
          {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </SettingsPage>
  );
}
